<svelte:options runes={false} />
<script>
  // The ONE Auto Trading dashboard (U9, venue-first per ADR-0011; target-scoped since ADR-0040). It owns the
  // shared mechanics — strategy list + selection, authoritative refetch, lifecycle actions, deploy flow, SSE
  // subscription, backfill poll, the org/target freshness guards — and is scoped by a RUN TARGET {venue, mode}
  // derived from the header context (lib/runContext.js `targetFor`): the Live context lists (Coinbase, Live),
  // its Paper Trading view (`view="paper"`) lists (Coinbase, Paper), the Sandbox context lists (Sandbox, Live).
  // There are no per-mode shells — the target IS the separation (a Paper and a Live run on the same venue are
  // two books the server never mixes).
  //
  // SAFETY POSTURE: safety labeling derives from the strategy's PERSISTED target (selectedStrategyTarget —
  // `exchange` + `mode` on the snapshot), never from which view is open. PROMOTION (D4) is a FRESH DEPLOY at
  // (Coinbase, Live) behind the typed-name confirm — never a flag flip on a running strategy; the simulated
  // strategy's history stays with it. ANY deploy whose target is real money is held behind the typed-name
  // confirm, regardless of which view requested it.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../lib/api.js';
  import { selectedOrgId } from '../../lib/orgContext.js';
  import { tradingContext, setTradingContext } from '../../lib/venueContext.js';
  import {
    MODE, targetFor, targetOf, isRealMoney, targetBadge, sameTarget, targetKey, matchesTarget, setStreamModeFilter,
    streamModeFilter,
  } from '../../lib/runContext.js';
  import { ensureSse, addSseSubscriber, pruneAlertsToStrategies } from '../../lib/sseStore.js';
  import { formatQuoteAmount } from '../../lib/quoteCurrency.js';
  import { summarizeSymbols } from '../../lib/symbolUniverse.js';
  import {
    autotradeStrategies, selectedStrategyId, selectedStrategy, strategyStatus, selectedStrategyTarget,
    strategyPerformance, strategyLoading, strategyError, resetAutotradeSelection, conflictStrategyId,
    surface, applyAdjustmentStatus, bumpNavTick, bumpDecisionTick,
  } from '../../lib/autotradeStore.js';
  import { get } from 'svelte/store';

  import StrategyDeployForm from './StrategyDeployForm.svelte';
  import RunningStrategiesList from './RunningStrategiesList.svelte';
  import StrategyWorkspace from './StrategyWorkspace.svelte';
  import Portfolio from './Portfolio.svelte';
  import FakeHundredBill from './FakeHundredBill.svelte';
  import RealHundredBill from './RealHundredBill.svelte';
  import SlideInPanel from './SlideInPanel.svelte';

  /**
   * Which view of the header context this dashboard instance is (ADR-0012 AC 3 / ADR-0040): `null` for the
   * context's own target ((Coinbase, Live) or (Sandbox, Live)); `'paper'` for the Paper Trading view —
   * (Coinbase, Paper): simulated fills against the live book, while the header stays in the Live context.
   */
  export let view = null;
  $: viewTarget = targetFor($tradingContext, view);

  /** This view shows real money (drives labels/banner; the list filter uses the whole target). */
  $: isLiveView = isRealMoney(viewTarget);
  /** This is the Paper Trading view (Paper mode — simulated fills). */
  $: isPaperView = viewTarget.mode === MODE.PAPER;
  /** The real-money target a promotion deploys at (D4). */
  const LIVE_TARGET = targetFor('live');

  let listLoading = false;
  let listError = null;
  let deploying = false;
  let deployError = null;
  // #2: New Strategy is a slide-in overlay (the workspace is always the base view), not a full-page swap.
  let newStrategyOpen = false;
  let actionBusy = false;
  let formComponent;
  // Tracks whether the async strategy load has resolved at least once — until then we never flash an empty
  // workspace (the feasibility-review ordering caveat, U11).
  let countLoaded = false;

  // ANY deploy targeting real money holds its request behind the typed-name confirm.
  let liveDeployConfirm = null;
  let liveDeployTyped = '';

  // Promotion (D4): deploy a FRESH instance of the selected simulated strategy at (Coinbase, Live).
  // `promoteInitial` prefills the deploy form from the selected strategy's snapshot; the form targets LIVE_TARGET.
  let promoteInitial = null;
  let promotedNotice = null; // "strategy X deployed live" banner after a successful promotion
  // TASK-20260823 AC 11: an ACCEPTED deploy's caveat (trading ahead of the TWAP schedule rests the child-order
  // rate on the concurrent-parent caps alone) — the 201 body's `warning`, shown until dismissed.
  let deployWarning = null;

  $: result = $strategyPerformance;

  // SAFETY: per-strategy labeling derives from the freshly-refetched PERSISTED target of the SELECTED strategy,
  // never from which view is open. Fail-closed: a strategy whose target the SPA cannot classify is neither
  // live nor silently paper — it renders the UNKNOWN badge and the mismatch banner below.
  $: isLiveSelected = isRealMoney($selectedStrategyTarget);
  $: selectedMatchesView = $selectedStrategy ? sameTarget($selectedStrategyTarget, viewTarget) : true;

  // Promotion is offered on a simulated (Paper or Sandbox) strategy that is actually running (mirrors the old
  // go-live gate); never on one whose target is unrecognised.
  $: canPromote = !isLiveView
    && $selectedStrategy
    && $selectedStrategyTarget != null
    && !isRealMoney($selectedStrategyTarget)
    && $strategyStatus === 'Running';

  // ---- Authoritative refetch (the SSE-triggered + on-connect callback). Captures the org at trigger time and
  //      DROPS a stale-org response (org-switch race guard, U3/U6). ----
  async function refetchAuthoritative() {
    const id = $selectedStrategyId;
    if (!id) return;
    const orgAtTrigger = $selectedOrgId;
    try {
      const [snapshot, perf] = await Promise.all([
        api.getStrategy(id, orgAtTrigger),
        api.getStrategyPerformance(id, orgAtTrigger),
      ]);
      // Drop a stale response if EITHER the org OR the selected strategy changed mid-flight — otherwise a slow
      // refetch for strategy X (or the previous org) could paint X's financials while Y is selected.
      if ($selectedOrgId !== orgAtTrigger || $selectedStrategyId !== id) return;
      if (snapshot) {
        autotradeStrategies.update((list) =>
          list.map((s) => (s.strategyId === id ? { ...s, ...snapshot } : s)));
      }
      strategyPerformance.set(perf);
      strategyError.set(null);
    } catch (err) {
      strategyError.set(mapError(err));
    }
  }

  async function loadStrategies() {
    // Same stale-response rule as refetchAuthoritative below (TASK-20260818): this fires from the
    // org-change reset, so a slow response for a superseded org must not paint that org's
    // strategy list into the shared store.
    const orgAtTrigger = $selectedOrgId;
    listLoading = true;
    listError = null;
    try {
      const all = (await api.getStrategies(orgAtTrigger)) ?? [];
      if ($selectedOrgId !== orgAtTrigger) return;
      // Prune persisted alerts for strategies that no longer exist (removed / undeployed) — across BOTH modes
      // (`all` is unfiltered), so a removed strategy's faults stop lingering in the feed without dropping the
      // other mode's live alerts. (#3 boundary fix.)
      pruneAlertsToStrategies(all.map((s) => s.strategyId));
      // Target separation (ADR-0011/0012 → ADR-0040): the list carries ONLY this view's target — Live
      // lists (2, Live), Paper Trading lists (2, Paper), Sandbox lists (6, Live); `s.exchange` + `s.mode`
      // are the persisted target. A row whose target is unrecognised matches NO view (fail-closed).
      // ALSO drop any terminal 'Undeployed' (fully removed) strategy — the backend already excludes it,
      // this is defense-in-depth so a stale snapshot can never render a removed strategy. 'Undeploying'
      // is KEPT (it shows "Removing…" until removal finalizes).
      const target = viewTarget;
      const mine = all.filter((s) =>
        String(s.status) !== 'Undeployed' && matchesTarget(s, target));
      autotradeStrategies.set(mine);
      countLoaded = true;
      if (mine.length >= 1 && !$selectedStrategyId) {
        await selectStrategy(mine[0].strategyId);
      }
    } catch (err) {
      listError = mapError(err);
    } finally {
      listLoading = false;
    }
  }

  async function selectStrategy(id) {
    selectedStrategyId.set(id);
    strategyPerformance.set(null);
    strategyLoading.set(true);
    try {
      await refetchAuthoritative();
    } finally {
      strategyLoading.set(false);
    }
  }

  function showNewStrategy() {
    promoteInitial = null; // a plain New Strategy defaults to this view's target (the form offers the others)
    newStrategyOpen = true;
  }

  // Deploy: a request whose target is REAL MONEY (Coinbase, Live) is HELD behind the typed-name confirm —
  // keyed on the request's `venue` + `mode`, not on which view is open — and only sent (`authorize: true`)
  // once the operator confirms. Paper and sandbox deploys send immediately (no real money).
  function handleDeploy(event) {
    if (isRealMoney(targetOf(event.detail))) {
      liveDeployTyped = '';
      liveDeployConfirm = event.detail;
      return;
    }
    void executeDeploy(event.detail);
  }
  function cancelLiveDeploy() {
    liveDeployConfirm = null;
    liveDeployTyped = '';
  }
  async function confirmLiveDeploy() {
    if (!liveDeployConfirm) return;
    // contract: DeployStrategyRequest.cs `authorize` — the explicit real-money authorization.
    const request = { ...liveDeployConfirm, authorize: true };
    liveDeployConfirm = null;
    await executeDeploy(request);
  }
  async function executeDeploy(request) {
    deploying = true;
    deployError = null;
    const wasPromotion = isRealMoney(targetOf(request)) && !isLiveView;
    try {
      const outcome = await api.deployStrategy(request, $selectedOrgId);
      deployWarning = outcome?.warning ?? null;
      if (wasPromotion) {
        // D4: the fresh real-money instance lives in the LIVE context's Live view — switch the app there so
        // the operator lands on what they just deployed (the simulated strategy keeps its own history).
        promotedNotice = request?.Name ?? 'Strategy';
        setTradingContext('live');
      }
      await loadStrategies();
      if (outcome?.strategyId) await selectStrategy(outcome.strategyId);
      newStrategyOpen = false;
      promoteInitial = null;
      formComponent?.resetToDefaults();
    } catch (err) {
      // Idempotent re-deploy (409): the same strategy+symbols is already deployed. Surface + select the
      // EXISTING strategy (the conflict body carries its id) instead of showing an error.
      const existingId = conflictStrategyId(err);
      if (existingId) {
        deployError = null;
        await loadStrategies();
        await selectStrategy(existingId);
        newStrategyOpen = false;
      } else {
        deployError = mapError(err);
      }
    } finally {
      deploying = false;
    }
  }

  // ---- Lifecycle actions (double-guarded by actionBusy + the control's own disabled states) ----
  async function runAction(fn) {
    if (actionBusy) return;
    actionBusy = true;
    try {
      await fn();
      await refetchAuthoritative();
      await loadStrategies();
    } catch (err) {
      strategyError.set(mapError(err));
    } finally {
      actionBusy = false;
    }
  }
  const onStart = () => runAction(() => api.startStrategy($selectedStrategyId, $selectedOrgId));
  const onPause = () => runAction(() => api.pauseStrategy($selectedStrategyId, $selectedOrgId));
  const onResume = () => runAction(() => api.resumeStrategy($selectedStrategyId, $selectedOrgId));
  const onStop = (e) => runAction(() => api.stopStrategy($selectedStrategyId, { flatten: e.detail.flatten }, $selectedOrgId));
  async function onRemove(e) {
    const id = $selectedStrategyId;
    // Remove (undeploy) begins TEARDOWN: the server moves the strategy to 'Undeploying' (transient) and the
    // supervisor finalizes it to 'Undeployed' (terminal/archived, ~10s later). We deliberately do NOT drop the
    // row optimistically — the operator should SEE it as "Removing…" until removal completes (then the backend
    // stops returning it and it drops on the next list read). On a failed POST the strategy is still running, so
    // we surface the error and leave the row untouched. (#3 / U2.)
    if (actionBusy) return;
    actionBusy = true;
    try {
      await api.undeployStrategy(id, { flatten: e.detail.flatten }, $selectedOrgId);
      // Reflect the new 'Undeploying' status immediately (the row stays, now showing "Removing…"), then poll
      // until the supervisor finalizes Undeploying → Undeployed and the backend drops it from the list.
      await loadStrategies();
      pollUntilRemoved(id);
    } catch (err) {
      // Undeploy POST failed — the strategy is STILL running on the backend. The row was never dropped, so it
      // stays visible; just surface the error.
      strategyError.set(mapError(err));
    } finally {
      actionBusy = false;
    }
  }

  // ---- Promotion (D4, replaces the retired setMode go-live): deploy a FRESH instance of the selected
  //      simulated strategy at (Coinbase, Live). Opens the deploy slide-in PREFILLED from the strategy's
  //      snapshot, targeting LIVE_TARGET — handleDeploy then holds it behind the typed-name real-money
  //      confirm. The simulated strategy (and its history/books) is untouched. ----
  function openPromote() {
    if (!canPromote) return;
    const s = $selectedStrategy;
    promoteInitial = {
      strategyKey: s?.strategy ?? null,
      name: s?.name ? `${s.name}-live` : null,
      symbols: s?.symbols ?? null,
      frequencySeconds: s?.frequencySeconds ?? null,
      enableStopOrders: s?.enableStopOrders,
      maxNotionalPerOrder: s?.maxNotionalPerOrder ?? null,
      maxOrdersPerWindow: s?.maxOrdersPerWindow ?? null,
      twap: s?.twap ?? null, // the sandbox strategy's EFFECTIVE TWAP policy seeds the live form (AC 11)
    };
    newStrategyOpen = true;
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    const idx = msg.indexOf('details: ');
    const body = idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
    // The backend speaks ProblemDetails (RFC 9457) for errors — surface its human `detail`/`title` instead of
    // dumping the raw JSON envelope (the operator-visible "{...traceId...}" noise).
    try {
      const parsed = JSON.parse(body);
      return parsed.detail || parsed.message || parsed.title || body;
    } catch {
      return body;
    }
  }

  // SSE: REGISTER this view's callbacks with the shared app-level connection (no churn on dashboard switch —
  // ensureSse is an idempotent no-op when already connected for this org). Unregistered on destroy.
  const sseCallbacks = { refetchAuthoritative, onAdjustmentStatus: applyAdjustmentStatus, onNavTick: bumpNavTick, onDecision: bumpDecisionTick };
  let sseUnsubscribe = null;

  // ---- Backfill/warmup poll: a freshly-deployed strategy is 'preparing' (candle backfill in flight)
  //      and a starting one is warming its candle cache — neither ticks yet, so SSE won't refresh
  //      them. Poll the list while either state exists so the live "collecting SYMBOL — batch j of M"
  //      detail (TASK-20260728 AC15) actually updates on screen. ----
  let backfillPollTimer = null;
  $: anyPreparing = $autotradeStrategies.some(
    (s) => s.backfillState === 'preparing' || s.status === 'Starting');
  $: if (anyPreparing && !backfillPollTimer) {
    backfillPollTimer = setInterval(loadStrategies, 3000);
  } else if (!anyPreparing && backfillPollTimer) {
    clearInterval(backfillPollTimer);
    backfillPollTimer = null;
  }

  // ---- Removal poll (#3 / U2): after a successful undeploy, re-poll the list until the removed id is gone or a
  //      timeout elapses. The window comfortably exceeds the supervisor's ~10s Stop→Stopped reconcile interval so
  //      a removal that follows a Stop still converges. Co-exists safely with the backfill poll (both call the
  //      idempotent loadStrategies). ----
  let removalPollTimer = null;
  function stopRemovalPoll() {
    if (removalPollTimer) { clearInterval(removalPollTimer); removalPollTimer = null; }
  }
  function pollUntilRemoved(id) {
    stopRemovalPoll(); // a fresh removal supersedes any in-flight removal poll
    const deadline = Date.now() + 30_000;
    removalPollTimer = setInterval(async () => {
      if (Date.now() > deadline) { stopRemovalPoll(); return; }
      // loadStrategies() overwrites the store from the server. While the strategy is 'Undeploying' the backend
      // KEEPS returning it (so it shows "Removing…"); once the supervisor finalizes it to 'Undeployed' the
      // backend drops it and the next read no longer carries the id. We do NOT force-hide it here — the operator
      // sees the live teardown. When it's gone, clear the selection if it was pointing at the removed strategy.
      await loadStrategies();
      const stillPresent = $autotradeStrategies.some((s) => s.strategyId === id);
      if (!stillPresent) {
        stopRemovalPoll();
        if ($selectedStrategyId === id) resetAutotradeSelection();
      }
    }, 2000);
  }

  // ---- Org/target freshness guards (M13: every reload and stale guard keys on (venue, mode)): clear the
  //      selection + reload the scoped list, re-scope the SSE stream. The target guard mirrors the org guard
  //      exactly — a Sandbox<->Live toggle must never leave the other target's selection, modals, or polls
  //      alive under the new context. ----
  let lastOrg = $selectedOrgId;
  $: if ($selectedOrgId !== lastOrg) {
    lastOrg = $selectedOrgId;
    resetForScopeChange();
  }
  // Initialize from the store expression directly — the $:-declared viewTarget is still undefined
  // while the instance script runs, and an undefined seed makes the guard fire spuriously on every
  // mount (duplicate loadStrategies/ensureSse alongside onMount's own calls).
  let lastTargetKey = targetKey(targetFor(get(tradingContext), view));
  $: if (targetKey(viewTarget) !== lastTargetKey) {
    lastTargetKey = targetKey(viewTarget);
    resetForScopeChange();
  }
  function resetForScopeChange() {
    resetAutotradeSelection();
    stopRemovalPoll(); // a pending removal poll belongs to the previous scope's list
    newStrategyOpen = false;
    liveDeployConfirm = null;
    promoteInitial = null;
    promotedNotice = null;
    countLoaded = false;
    loadStrategies();
    ensureSse(); // re-scopes to the new org + (venue, mode) (reconnects only on a real scope change)
  }

  // The Paper Trading view is mode-specific: it narrows the shared SSE stream with `?mode=Paper` (ADR-0040
  // M12) so Live-run events never light up a paper screen. The other views leave the stream unscoped
  // (the manual trading surfaces share the connection and are Live). sseStore re-scopes the open
  // connection when the filter changes; the destroy hook clears only its OWN filter so the mount/destroy
  // order of two sibling dashboards cannot leave a stale filter behind.
  const myModeFilter = view === 'paper' ? MODE.PAPER : null;

  onMount(() => {
    setStreamModeFilter(myModeFilter);
    loadStrategies();
    ensureSse();
    sseUnsubscribe = addSseSubscriber(sseCallbacks);
  });
  onDestroy(() => {
    resetAutotradeSelection();
    if (backfillPollTimer) clearInterval(backfillPollTimer);
    stopRemovalPoll();
    if (sseUnsubscribe) sseUnsubscribe();
    if (myModeFilter !== null && get(streamModeFilter) === myModeFilter) setStreamModeFilter(null);
  });
</script>

<div class="at-view" class:at-view-live={isLiveView} class:at-view-paper={!isLiveView}>
  {#if isLiveView}
    <!-- Persistent, un-scrollable banner (sticky, AA-contrast, accessible name). It reflects the SELECTED
         strategy's actual persisted target, NOT just the fact that the Live view is open (defense in
         depth — the target filter should make the mismatch case impossible). -->
    {#if $selectedStrategy && !isLiveSelected}
      <div class="at-paper-in-live-banner" role="note" aria-label="Viewing a strategy that is not trading real money">
        <span aria-hidden="true">●</span> {targetBadge($selectedStrategyTarget)} — the selected strategy is not trading real money
      </div>
    {:else}
      <div class="at-live-banner" role="note" aria-label="Live trading — real money">
        <span aria-hidden="true">●</span> LIVE — REAL MONEY
        <RealHundredBill height={26} />
      </div>
    {/if}
  {/if}

  <div class="at-view-header">
    {#if isLiveView}
      <!-- No redundant title (TASK-20260721 item 3): the nav already reads "Live Trading" and the
           sticky LIVE banner above identifies the view; Paper/Sandbox keep theirs (salience). -->
    {:else if isPaperView}
      <FakeHundredBill height={44} />
      <h2 class="at-view-title">Paper Trading</h2>
      <span class="at-sim-note">PAPER — simulated fills (book-walk VWAP against the live Coinbase order book); no order ever reaches an exchange.</span>
      {#if $selectedStrategy && !selectedMatchesView}
        <span class="at-sim-note" role="note">Selected strategy runs {targetBadge($selectedStrategyTarget)} — not this view's target.</span>
      {/if}
    {:else}
      <FakeHundredBill height={44} />
      <h2 class="at-view-title">Auto Trading — Sandbox</h2>
      <span class="at-sim-note">Coinbase Sandbox venue — real matching engine, fake funds.</span>
      {#if $selectedStrategy && !selectedMatchesView}
        <span class="at-sim-note" role="note">Selected strategy runs {targetBadge($selectedStrategyTarget)} — not this view's target.</span>
      {/if}
    {/if}

    <!-- Top-level surface switch (U15): the per-strategy Workspace vs the cross-strategy Portfolio. -->
    <div class="at-surface-switch" role="tablist" aria-label="Dashboard surface">
      <button type="button" role="tab" class="at-surface-btn" class:active={$surface === 'workspace'}
        aria-selected={$surface === 'workspace'} on:click={() => surface.set('workspace')}>Strategies</button>
      <button type="button" role="tab" class="at-surface-btn" class:active={$surface === 'portfolio'}
        aria-selected={$surface === 'portfolio'} on:click={() => surface.set('portfolio')}>Portfolio</button>
    </div>
  </div>

  {#if promotedNotice}
    <div class="at-promoted-notice" role="status">
      <strong>{promotedNotice}</strong> is deployed LIVE on Coinbase (see Live Trading) — the original strategy and its history stay here.
      <button type="button" class="btn btn-ghost btn-sm" on:click={() => (promotedNotice = null)}>Dismiss</button>
    </div>
  {/if}

  {#if deployWarning}
    <div class="at-deploy-warning" role="status">
      <strong>Deployed with a caveat:</strong> {deployWarning}
      <button type="button" class="btn btn-ghost btn-sm" on:click={() => (deployWarning = null)}>Dismiss</button>
    </div>
  {/if}

  {#if $surface === 'portfolio'}
    <!-- Target separation (ADR-0011/0012 → ADR-0040): the portfolio aggregates ONLY this view's target. -->
    <Portfolio target={viewTarget} />
  {:else}
    <!-- The Strategy workspace is ALWAYS the base view (#2). New Strategy is a slide-in overlay. -->
    <div class="at-dashboard">
      <div class="at-dashboard-body">
        <div class="at-dashboard-left">
          <button type="button" class="btn btn-primary at-new-btn" on:click={showNewStrategy}
            title={isLiveView ? 'Deploy a new live strategy' : 'Deploy a new strategy'}>
            + New Strategy
          </button>
          <RunningStrategiesList
            strategies={$autotradeStrategies}
            selectedId={$selectedStrategyId}
            loading={listLoading}
            error={listError}
            on:select={(e) => selectStrategy(e.detail)}
            on:refresh={loadStrategies} />
        </div>

        <div class="at-dashboard-right">
          {#if $selectedStrategy}
            <StrategyWorkspace
              strategy={$selectedStrategy}
              status={$strategyStatus}
              {result}
              loading={$strategyLoading}
              error={$strategyError}
              {actionBusy}
              isLive={isLiveSelected}
              {onStart} {onPause} {onResume} {onStop} {onRemove}
              onGoLive={isLiveView ? null : openPromote}
              canGoLive={canPromote}
              onRetry={refetchAuthoritative} />
          {:else if countLoaded && $autotradeStrategies.length === 0}
            <div class="empty-state at-empty-cta">
              <p class="empty-state-description">No {isLiveView ? 'live' : isPaperView ? 'paper' : 'sandbox'} strategies for this target yet.</p>
              <button type="button" class="btn btn-primary btn-lg" on:click={showNewStrategy}>+ New Strategy</button>
            </div>
          {:else}
            <div class="empty-state">
              <p class="empty-state-description">Select a deployed strategy to see its workspace.</p>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- #2: New Strategy slide-in overlay (over the workspace). The form DEFAULTS to this view's target and
       offers the other deployable targets; a promotion (promoteInitial set) is pinned to (Coinbase, Live) —
       the form target follows the TARGET, not the open view. -->
  <SlideInPanel open={newStrategyOpen}
    title={promoteInitial ? 'Promote to Live on Coinbase' : (isLiveView ? 'New Live Strategy' : isPaperView ? 'New Paper Strategy' : 'New Sandbox Strategy')}
    on:close={() => { newStrategyOpen = false; promoteInitial = null; }}>
    {#if newStrategyOpen}
      <StrategyDeployForm bind:this={formComponent}
        target={promoteInitial ? LIVE_TARGET : viewTarget}
        lockTarget={!!promoteInitial}
        initial={promoteInitial} {deploying}
        serverError={deployError} on:deploy={handleDeploy} on:reset={() => formComponent?.resetToDefaults()} />
    {/if}
  </SlideInPanel>

  <!-- Real-money deploy confirm — typed strategy name authorizes ANY deploy targeting (Coinbase, Live). -->
  {#if liveDeployConfirm}
    <div class="at-modal-backdrop" on:click|self={cancelLiveDeploy}>
      <div class="at-modal at-go-live-modal" role="dialog" aria-modal="true" aria-labelledby="live-deploy-title">
        <h3 id="live-deploy-title" class="at-modal-title at-go-live-title">⚠ Deploy Live — real money</h3>
        <p class="at-modal-body">
          This deploys <strong>{liveDeployConfirm.Name}</strong> ({summarizeSymbols(liveDeployConfirm.Symbols, 4) || 'universe selected by the strategy'}) on the
          <strong>live Coinbase venue (Live mode)</strong>. Once started it places <strong>real orders</strong> with
          {formatQuoteAmount(
            (liveDeployConfirm.Allocations ?? []).reduce((s, a) => s + Number(a.StartingCash || 0), 0),
            liveDeployConfirm.QuoteCurrency ?? 'USD')}
          of real funds.
        </p>
        <label class="at-go-live-confirm-field">
          <span>Type the strategy name <strong>{liveDeployConfirm.Name}</strong> to confirm:</span>
          <input type="text" class="form-input" bind:value={liveDeployTyped} autocomplete="off"
            aria-label="Type the strategy name to confirm the live deploy" />
        </label>
        <div class="at-modal-actions">
          <button type="button" class="btn btn-ghost" on:click={cancelLiveDeploy}>Cancel</button>
          <button type="button" class="btn at-go-live-btn"
            disabled={liveDeployTyped.trim() !== liveDeployConfirm.Name}
            on:click={confirmLiveDeploy}>Deploy Live</button>
        </div>
      </div>
    </div>
  {/if}

</div>

<style>
  @import '../Backtesting/Backtesting.css';
  .at-surface-switch { margin-left: auto; display: inline-flex; gap: 0.25rem; }
  .at-surface-btn {
    appearance: none; background: transparent; border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 6px; padding: 0.35rem 0.85rem; font-size: 0.8rem; font-weight: 600;
    color: var(--text-muted, #8a8b8d); cursor: pointer;
  }
  .at-surface-btn.active { color: var(--text-primary, #e8e9ea); border-color: var(--accent, #3c78f0); }
  .at-view-live .at-surface-btn.active { border-color: #f0594a; }
  .at-new-btn { width: 100%; margin-bottom: 0.75rem; }
  .at-empty-cta { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 2.5rem 1rem; }
  .at-promoted-notice {
    display: flex; align-items: center; gap: 0.6rem;
    padding: 0.5rem 0.85rem; margin-bottom: 0.75rem;
    border: 1px solid rgba(240, 90, 70, 0.4); border-radius: 6px;
    background: rgba(240, 90, 70, 0.08); font-size: 0.85rem; color: var(--text-primary, #e8e9ea);
  }
  /* The accepted-with-a-caveat notice (TWAP trade-ahead): warning tone, never colour-only (the label says it). */
  .at-deploy-warning {
    display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
    padding: 0.5rem 0.85rem; margin-bottom: 0.75rem;
    border: 1px solid var(--border-primary, #2a2b2e); border-left: 3px solid var(--color-warning, #d99a2b);
    border-radius: 6px; background: var(--bg-secondary, #141517);
    font-size: 0.85rem; color: var(--text-secondary, #b5b6b8);
  }
  .at-deploy-warning strong { color: var(--text-primary, #e8e9ea); }
</style>
